import type {
  BodyId,
  FlightPhase,
  TelemetrySample,
  TrajectoryReferenceFrame,
} from '@trajectory/astro';

export const BODY_IDS = ['earth', 'moon', 'sun', 'mars', 'venus', 'jupiter'] as const satisfies readonly BodyId[];
export const REFERENCE_FRAMES = [
  'earth-inertial',
  'earth-centered',
  'heliocentric',
  'body-centered',
] as const satisfies readonly TrajectoryReferenceFrame[];
export const FLIGHT_PHASES = [
  'pre-launch',
  'vertical-rise',
  'pitch-over',
  'gravity-turn',
  'coast-to-apoapsis',
  'circularisation',
  'orbit',
  'parking-orbit',
  'departure',
  'trans-lunar',
  'interplanetary-coast',
  'arrival',
  'destination-orbit',
  'maneuver',
  'reentry',
  'landed',
] as const satisfies readonly FlightPhase[];

const bodyCode = new Map(BODY_IDS.map((value, index) => [value, index]));
const frameCode = new Map(REFERENCE_FRAMES.map((value, index) => [value, index]));
const phaseCode = new Map(FLIGHT_PHASES.map((value, index) => [value, index]));

/**
 * The precomputed flight as columns of numbers rather than an array of
 * per-instant objects. `propagateFlight` returns a dense `TelemetrySample[]`
 * for the *entire* flight in one batch (the physics core is deterministic and
 * has no notion of "live" — see the module doc on `sim.worker.ts`), so there
 * is no ring buffer to maintain: this conversion happens once, and every
 * chart then reads a `subarray(0, index)` slice of an already-materialized
 * typed array on each animation frame, imperatively, without going through
 * React state.
 */
export interface TelemetrySeries {
  t: Float64Array;
  epoch: Float64Array;
  altitude: Float64Array;
  speed: Float64Array;
  relativeSpeed: Float64Array;
  dynamicPressure: Float64Array;
  gLoad: Float64Array;
  mass: Float64Array;
  throttle: Float64Array;
  mach: Float64Array;
  apoapsisAltitude: Float64Array;
  periapsisAltitude: Float64Array;
  flightPathAngleDeg: Float64Array;
  pitchDeg: Float64Array;
  angleOfAttackDeg: Float64Array;
  deltaVExpended: Float64Array;
  dragLoss: Float64Array;
  gravityLoss: Float64Array;
  /** Geodetic latitude/longitude, degrees — for the 2D map and 3D globe ground track. */
  latitudeDeg: Float64Array;
  longitudeDeg: Float64Array;
  /** ECI position, metres — for the 3D globe, which renders in the same inertial frame as the orbit ribbon. */
  positionX: Float64Array;
  positionY: Float64Array;
  positionZ: Float64Array;
  /** Indices into BODY_IDS, REFERENCE_FRAMES, and FLIGHT_PHASES. */
  centralBodyCode: Uint8Array;
  referenceFrameCode: Uint8Array;
  phaseCode: Uint8Array;
}

const RAD_TO_DEG = 180 / Math.PI;

export function samplesToSeries(samples: TelemetrySample[]): TelemetrySeries {
  const n = samples.length;
  const series: TelemetrySeries = {
    t: new Float64Array(n),
    epoch: new Float64Array(n),
    altitude: new Float64Array(n),
    speed: new Float64Array(n),
    relativeSpeed: new Float64Array(n),
    dynamicPressure: new Float64Array(n),
    gLoad: new Float64Array(n),
    mass: new Float64Array(n),
    throttle: new Float64Array(n),
    mach: new Float64Array(n),
    apoapsisAltitude: new Float64Array(n),
    periapsisAltitude: new Float64Array(n),
    flightPathAngleDeg: new Float64Array(n),
    pitchDeg: new Float64Array(n),
    angleOfAttackDeg: new Float64Array(n),
    deltaVExpended: new Float64Array(n),
    dragLoss: new Float64Array(n),
    gravityLoss: new Float64Array(n),
    latitudeDeg: new Float64Array(n),
    longitudeDeg: new Float64Array(n),
    positionX: new Float64Array(n),
    positionY: new Float64Array(n),
    positionZ: new Float64Array(n),
    centralBodyCode: new Uint8Array(n),
    referenceFrameCode: new Uint8Array(n),
    phaseCode: new Uint8Array(n),
  };

  for (let i = 0; i < n; i++) {
    const sample = samples[i]!;
    series.t[i] = sample.t;
    series.epoch[i] = sample.epoch;
    series.altitude[i] = sample.altitude;
    series.speed[i] = sample.speed;
    series.relativeSpeed[i] = sample.relativeSpeed;
    series.dynamicPressure[i] = sample.dynamicPressure;
    series.gLoad[i] = sample.gLoad;
    series.mass[i] = sample.mass;
    series.throttle[i] = sample.throttle;
    series.mach[i] = sample.mach;
    series.apoapsisAltitude[i] = Number.isFinite(sample.apoapsisAltitude) ? sample.apoapsisAltitude : NaN;
    series.periapsisAltitude[i] = sample.periapsisAltitude;
    series.flightPathAngleDeg[i] = sample.flightPathAngle * RAD_TO_DEG;
    series.pitchDeg[i] = sample.pitch * RAD_TO_DEG;
    series.angleOfAttackDeg[i] = sample.angleOfAttack * RAD_TO_DEG;
    series.deltaVExpended[i] = sample.deltaVExpended;
    series.dragLoss[i] = sample.dragLoss;
    series.gravityLoss[i] = sample.gravityLoss;
    series.latitudeDeg[i] = sample.latitude * RAD_TO_DEG;
    series.longitudeDeg[i] = sample.longitude * RAD_TO_DEG;
    series.positionX[i] = sample.position[0];
    series.positionY[i] = sample.position[1];
    series.positionZ[i] = sample.position[2];
    const centralBody = sample.centralBodyId ?? 'earth';
    const referenceFrame =
      sample.referenceFrame ?? (centralBody === 'earth' ? 'earth-inertial' : 'body-centered');
    series.centralBodyCode[i] = bodyCode.get(centralBody) ?? 0;
    series.referenceFrameCode[i] = frameCode.get(referenceFrame) ?? 0;
    series.phaseCode[i] = phaseCode.get(sample.phase) ?? 0;
  }

  return series;
}

/** Index of the last sample at or before `time` — a binary search since `t` is monotonically increasing. */
export function findIndexForTime(t: Float64Array, time: number): number {
  if (t.length === 0) return -1;
  let lo = 0;
  let hi = t.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (t[mid]! <= time) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

export const TELEMETRY_CHANNEL_KEYS = [
  't',
  'epoch',
  'altitude',
  'speed',
  'relativeSpeed',
  'dynamicPressure',
  'gLoad',
  'mass',
  'throttle',
  'mach',
  'apoapsisAltitude',
  'periapsisAltitude',
  'flightPathAngleDeg',
  'pitchDeg',
  'angleOfAttackDeg',
  'deltaVExpended',
  'dragLoss',
  'gravityLoss',
  'latitudeDeg',
  'longitudeDeg',
  'positionX',
  'positionY',
  'positionZ',
  'centralBodyCode',
  'referenceFrameCode',
  'phaseCode',
] as const satisfies readonly (keyof TelemetrySeries)[];
